import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Divider,
  Checkbox,
  Link,
  Input,
} from '@heroui/react';
import { ThemeSwitcher } from '@/components/shared/ThemeSwitcher';
import TermsModal from '@/components/shared/TermsModal';
import PrivacyPolicyModal from '@/components/shared/PrivacyPolicyModal';
import macroLogo from '@/assets/logo-macroindustrial-tr.png';
import { getUserEmpresaByTokenInvitation, linkEmpresaAUsuario } from '@/services/empresa/users.service';
import { createUserEmpresa } from '@/services/empresa/users.service';
import { useNavigate } from 'react-router-dom';

const Verify = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [decodedEmail, setDecodedEmail] = useState<string | null>(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    // Extract token and email from URL parameters
    const tokenParam = searchParams.get('token');
    const emailParam = searchParams.get('email');
    
    setToken(tokenParam);
    
    // Decode the email parameter if it's URL-encoded (like in your example)
    if (emailParam) {
      try {
        const decoded = decodeURIComponent(emailParam);
        setDecodedEmail(decoded);
      } catch (error) {
        console.error('Error decoding email parameter:', error);
        setDecodedEmail(emailParam);
      }
    }
  }, [searchParams]);



  const validatePasswords = () => {
    if (password !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validatePasswords() || !isTermsAccepted) {
      return;
    }

    setIsLoading(true);
    try {
      // Create user with the invitation token
      const usuarioNuevo = await createUserEmpresa({
        email: decodedEmail || '',
        password,
        token: token || ''
      });
      
      const empresaUsuarioId = await getUserEmpresaByTokenInvitation(token || '');

      if(!empresaUsuarioId || !empresaUsuarioId.tenantId){
        alert('Registro de invitación no existente')
        navigate('/')
      }

      linkEmpresaAUsuario(empresaUsuarioId?.tenantId ?? '', usuarioNuevo.data.id)

      // Redirect to login page after successful registration
      navigate('/');
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error al crear el usuario. Por favor inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl bg-white dark:bg-gray-800">
          <CardHeader className="flex flex-col items-center py-8">
            <div className="w-full flex justify-end pr-4">
              <ThemeSwitcher />
            </div>
            <div className="flex justify-center mb-4">
              <img 
                src={macroLogo} 
                alt="MacroIndustrial" 
                className="h-16 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Verificación de Cuenta</h1>
            <p className="text-gray-600 dark:text-gray-300 text-center mt-2">
              Crea tu cuenta y contraseña para completar tu registro
            </p>
          </CardHeader>
          
          <Divider className="dark:bg-gray-700" />
          
          <CardBody className="py-6 px-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3">Información de Verificación</h2>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="bg-blue-50 dark:bg-gray-700 p-2 rounded-full mr-3">
                    <svg className="w-5 h-5 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Email a verificar</p>
                    <p className="font-medium text-gray-800 dark:text-white">{decodedEmail || 'No proporcionado'}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-green-50 dark:bg-gray-700 p-2 rounded-full mr-3">
                    <svg className="w-5 h-5 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Token de verificación</p>
                    <p className="font-mono text-xs text-gray-600 dark:text-gray-300 break-all">{token ? `${token.substring(0, 8)}...${token.substring(token.length - 8)}` : 'No proporcionado'}</p>
                  </div>
                </div>
              </div>
            </div>
           
            <div className="space-y-4">
              <Input
                label="Contraseña"
                type="password"
                variant="bordered"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isRequired
                errorMessage={passwordError && passwordError.includes('contraseña') ? passwordError : ''}
              />
              <Input
                label="Confirmar Contraseña"
                type="password"
                variant="bordered"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                isRequired
                errorMessage={passwordError && passwordError.includes('coincid') ? passwordError : ''}
              />
            </div>
          </CardBody>
          
          <CardFooter className="flex flex-col py-6 px-8">
            <Checkbox
              isSelected={isTermsAccepted}
              onValueChange={setIsTermsAccepted}
              className="mb-4"
            >
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Acepto los{' '}
                <Link 
                  className="text-blue-500 hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsTermsModalOpen(true);
                  }}
                >
                  Términos de Servicio
                </Link>{' '}
                y la{' '}
                <Link 
                  className="text-blue-500 hover:underline cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsPrivacyModalOpen(true);
                  }}
                >
                  Política de Privacidad
                </Link>.
              </span>
            </Checkbox>
            
            <Button 
              variant="solid"
              onPress={handleSubmit}
              isDisabled={!isTermsAccepted || !password || !confirmPassword}
              isLoading={isLoading}
            >
              Crear Cuenta de Empresa
            </Button>
{/* 
            <Button 
              variant="bordered" 
              onPress={handleGoogleLogin}
              className="mt-4"
            >
              <div className="flex items-center justify-center">
                <img src={googleLogo} alt="Google" className="w-5 h-5 mr-3" />
                Continuar con Google
              </div>
            </Button> */}
            
            <p className="text-xs text-gray-500 mt-4 text-center dark:text-gray-400">
              By signing up or signing in, you agree to our{' '}
              <Link 
                className="text-xs text-blue-500 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setIsTermsModalOpen(true);
                }}
              >
                Terms
              </Link>{' '}
              and{' '}
              <Link 
                className="text-xs text-blue-500 hover:underline cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  setIsPrivacyModalOpen(true);
                }}
              >
                Privacy Policy
              </Link>.
            </p>
          </CardFooter>
        </Card>
        
        {/* Terms Modal */}
        <TermsModal 
          isOpen={isTermsModalOpen} 
          onClose={() => setIsTermsModalOpen(false)} 
        />
        
        {/* Privacy Policy Modal */}
        <PrivacyPolicyModal 
          isOpen={isPrivacyModalOpen} 
          onClose={() => setIsPrivacyModalOpen(false)} 
        />
      </div>
    </div>
  );
};

export default Verify;